import React, { ReactNode } from 'react';

import { AvatarPropTypes } from '@atlaskit/avatar';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { RelativeDate } from '@atlassian/bitkit-date';

import { User } from 'src/components/types';
import {
  DeletedUserAvatar,
  DeletedUserName,
  UserAvatar,
  UserName,
} from 'src/components/user-profile';

type Props = {
  actor: User | undefined;
  children: ReactNode;
  date: string | Date;
  title: ReactNode;
  renderActorAvatar: (
    actor: User | undefined,
    avatarProps: Partial<AvatarPropTypes>
  ) => ReactNode;
  renderActorName: (actor: User | undefined) => ReactNode;
};

const activityCardText = xcss({
  display: 'inline-block',
});

const activityCardProfileIcon = xcss({
  display: 'inline-block',
  verticalAlign: 'middle',
});

export const ActivityEvent = ({
  renderActorAvatar = (
    actor: User | undefined,
    avatarProps: Partial<AvatarPropTypes>
  ) => {
    if (!actor) {
      return <DeletedUserAvatar {...avatarProps} />;
    }
    return <UserAvatar {...avatarProps} user={actor} />;
  },
  renderActorName = (actor: User | undefined) => {
    if (!actor) {
      return <DeletedUserName />;
    }
    return <UserName user={actor} />;
  },
  ...restProps
}: Props): JSX.Element => {
  const { actor, children, date, title } = restProps;

  return (
    <Box paddingBlock="space.100" data-qa="pull-request-activity">
      <Stack space="space.100">
        <Box>
          <Box xcss={activityCardProfileIcon}>
            {renderActorAvatar(actor, { size: 'medium' })}
          </Box>
          <Box paddingInline="space.050" xcss={activityCardText}>
            {renderActorName(actor)}
          </Box>
          {title}
          <Box paddingInline="space.050" xcss={activityCardText}>
            <Text color="color.text.subtle" as="span">
              <RelativeDate date={date} />
            </Text>
          </Box>
        </Box>
        <Box>
          <div
            data-qa="pull-request-overview-activity-content"
            style={{ position: 'relative' }}
          >
            {children}
          </div>
        </Box>
      </Stack>
    </Box>
  );
};
